// Client-side simulation engine — a browser port of the server's heuristic
// orchestration. Lets the four-panel studio run with NO backend (static hosting,
// no API, no booting). When the Express backend IS reachable it's used instead;
// this is the resilient fallback.

const SECONDS_PER_MONTH = 1
const CHANGELOG_EVERY_MONTHS = 3
const PALETTES = ['#7df9ff', '#b388ff', '#ff8a65', '#69f0ae']

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

// Real, cited baselines for San Francisco (not mock values).
function sfIndicators() {
  return {
    year: 2026, month: 0,
    population: { value: 827526, source: 'Data Commons / US Census 2024', url: 'https://datacommons.org/place/geoId/0667000', unit: 'people' },
    homelessness: { value: 8323, source: 'SF 2024 Point-in-Time Count', url: 'https://www.sf.gov/resource--2024--san-francisco-point-time-pit-count', unit: 'unhoused' },
    hunger: { value: 25, source: 'SF-Marin Food Bank — ~1 in 4 at risk', url: 'https://www.sfmfoodbank.org/hunger-report-2024/', unit: '% food-insecure' },
    jobs: { value: 96.3, source: 'BLS/FRED — SF County 3.7% unemployment', url: 'https://fred.stlouisfed.org/series/CASANF0URN', unit: '% employed' },
    aiDisplacement: { value: 25, source: 'ILO 2024 — 25% jobs exposed to GenAI', url: 'https://www.ilo.org/resource/news/one-four-jobs-risk-being-transformed-genai-new-ilo-nask-global-index-shows', unit: '% exposed' },
    housingBuilt: { value: 0, source: 'cumulative this run', url: '', unit: 'units' },
    seismicRisk: { value: 0.72, source: 'USGS — 72% M6.7+ Bay Area in 30yr', url: 'https://www.usgs.gov/faqs/what-probability-earthquake-will-occur-los-angeles-area-san-francisco-bay-area', unit: 'probability' }
  }
}

// Parse a real quantity from a prompt: "1,000 homes", "10k", "2 million".
export function parseQty(text) {
  const m = String(text || '').match(/([\d][\d,]*(?:\.\d+)?)\s*(k|thousand|m|mn|million|bn|billion)?/i)
  if (!m) return null
  let n = parseFloat(m[1].replace(/,/g, '')); if (isNaN(n)) return null
  const s = (m[2] || '').toLowerCase()
  if (s === 'k' || s === 'thousand') n *= 1e3
  else if (s === 'm' || s === 'mn' || s === 'million') n *= 1e6
  else if (s === 'bn' || s === 'billion') n *= 1e9
  return Math.round(n)
}

export function defaultProjection() {
  return {
    id: 'sf-future', title: 'San Francisco — AI Future Trajectory',
    baselineQuestion: 'What does the future of San Francisco look like on the current trajectory of AI?',
    domain: 'city', indicators: sfIndicators()
  }
}
export function customProjection({ id, title, prompt }) {
  return {
    id: id || 'custom', title: title || 'Custom Research Projection', baselineQuestion: prompt, domain: 'generic',
    indicators: { year: 2026, month: 0, index: { value: 50, unit: '0-100' }, pressure: { value: 0.3, unit: '0-1' }, novelty: { value: 0.5, unit: '0-1' } }
  }
}

function neutralWorld(seed = 0) {
  return {
    mood: 'neutral', weather: 'clear', palette: PALETTES[seed % 4],
    buildingDensity: 0.55, buildingHeight: 0.5, shininess: 0.4,
    flyingCars: 0, tents: 0.15, destruction: 0, greenery: 0.3, fog: 0.25, timeOfDay: 0.5
  }
}
function makeAgent(window) {
  return {
    window, name: `WINDOW ${window}`, status: 'idle', thesis: 'forming an interpretation…',
    interpretation: '', worldState: neutralWorld(window - 1), variables: [], improvementScore: 0.5,
    divergence: 0, lastCritiqueGiven: null, lastCritiqueReceived: null, model: 'local-heuristic'
  }
}
export function createState(projection = defaultProjection()) {
  return {
    runId: 'local-' + projection.id, projection, simSeconds: 0, monthsElapsed: 0, playing: false,
    agents: [1, 2, 3, 4].map(makeAgent), chat: [], changelog: [], events: [],
    asset: { used: 0, capacity: 10, windowStartSeconds: 0, generated: [] }, local: true
  }
}

function advanceIndicators(i, m) {
  if (!m) return
  if (i.population) {
    i.aiDisplacement.value = clamp(i.aiDisplacement.value + 0.35 * m, 0, 80)
    i.jobs.value = clamp(96.5 - i.aiDisplacement.value * 0.25, 60, 99)
    i.homelessness.value = clamp(i.homelessness.value + (i.aiDisplacement.value > 20 ? 40 : 8) * m * 0.1, 0, 60000)
    i.hunger.value = clamp(13 + (96.5 - i.jobs.value) * 0.4, 0, 60)
    i.population.value = clamp(i.population.value + (i.jobs.value > 90 ? 200 : -300) * m * 0.1, 200000, 1500000)
    i.seismicRisk.value = clamp(i.seismicRisk.value * 0.999, 0, 1)
  } else if (i.index) {
    i.index.value = clamp(i.index.value + (i.novelty.value - i.pressure.value) * 2 * m, 0, 100)
    i.pressure.value = clamp(i.pressure.value + 0.005 * m, 0, 1)
  }
  i.month += m; i.year = 2026 + Math.floor(i.month / 12)
}

const EVENT_RULES = [
  { rx: /(earthquake|quake|seismic|disaster|collapse)/i, key: 'quake' },
  { rx: /(regulat|\bban\b|\blaw\b|policy|govern|oversight)/i, key: 'regulated' },
  { rx: /(agi|ai boom|automat|superintellig|robot|tech boom)/i, key: 'aiboom' },
  { rx: /(flood|sea level|storm|heavy rain|tsunami)/i, key: 'flood' },
  { rx: /(fire|wildfire|smoke|burn)/i, key: 'fire' },
  { rx: /(snow|blizzard|winter|freeze)/i, key: 'snow' },
  { rx: /(disease|pandemic|plague|virus|outbreak)/i, key: 'disease' },
  { rx: /(ubi|basic income|prosper|abundance|utopia)/i, key: 'prosperity' },
  { rx: /(exodus|leave town|flee|depopulat|outflow|move away)/i, key: 'exodus' }
]
// Intelligent classification: distinguishes HOUSING BUILT (good) from a HOMELESS
// INFLUX / encampments (bad) — they share the word "home". Action verbs decide.
function eventKey(t) {
  const s = String(t).toLowerCase()
  for (const r of EVENT_RULES) if (r.rx.test(s)) return r.key
  const built = /(built|build|housed|construct|open(ed)?|complete|deliver)/.test(s)
  const housingNoun = /(housing|\bhome\b|\bhomes\b|shelter|unit|apartment|dwelling)/.test(s)
  const influx = /(homeless|unhoused|encampment|tent|migrant|newcomer|influx|arrive|relocat|move in)/.test(s)
  if (influx && !built) return 'influx'        // "3000 homeless arrive", "tents go up"
  if (housingNoun && built) return 'housing'   // "1000 homes built", "shelters opened"
  if (influx) return 'influx'
  if (housingNoun) return 'housing'
  return 'generic'
}

export function classifyMessage(text) {
  const targets = []; const re = /@([1-4])\b/g; let m
  while ((m = re.exec(text))) targets.push(Number(m[1]))
  return { targets: [...new Set(targets)], clean: text.replace(/@[1-4]\b/g, '').trim(), global: targets.length === 0 }
}

// qty = a real number parsed from the prompt ("1,000 homes" -> 1000); when
// absent, a sensible default is used so events still register.
function applyEvent(i, key, g = 1, qty = null) {
  if (!i.population) return
  switch (key) {
    case 'quake': i.seismicRisk.value = 1; i.population.value = clamp(i.population.value - (qty || 40000) * g, 2e5, 15e5); i.homelessness.value = clamp(i.homelessness.value + 6000 * g, 0, 60000); break
    case 'housing': { const homes = Math.min(qty || 1000, 200000); i.homelessness.value = clamp(i.homelessness.value - homes * 0.7, 0, 60000); i.housingBuilt.value = clamp(i.housingBuilt.value + homes, 0, 5e6); break }
    case 'influx': { const ppl = Math.min(qty || 3000, 500000); i.homelessness.value = clamp(i.homelessness.value + ppl, 0, 200000); i.population.value = clamp(i.population.value + ppl, 2e5, 2e6); break }
    case 'regulated': i.aiDisplacement.value = clamp(i.aiDisplacement.value * 0.7, 0, 80); i.jobs.value = clamp(i.jobs.value + 3 * g, 60, 99); break
    case 'aiboom': i.aiDisplacement.value = clamp(i.aiDisplacement.value + (qty ? Math.min(qty / 1000, 40) : 12) * g, 0, 80); break
    case 'prosperity': i.hunger.value = clamp(i.hunger.value - 4 * g, 0, 60); i.homelessness.value = clamp(i.homelessness.value - (qty || 1200) * g, 0, 60000); break
    case 'disease': case 'exodus': i.population.value = clamp(i.population.value - (qty || 25000) * g, 2e5, 15e5); break
    case 'flood': case 'fire': case 'snow': i.homelessness.value = clamp(i.homelessness.value + (qty || 2000) * g, 0, 60000); break
  }
}

function interpret(ind, adopted, seed) {
  const ws = neutralWorld(seed)
  let thesis = 'Baseline San Francisco, trajectory unfolding.'
  const ai = ind.aiDisplacement?.value ?? 0, homeless = ind.homelessness?.value ?? 0
  const quake = (ind.seismicRisk?.value ?? 0) > 0.9
  const yrs = (ind.year || 2026) - 2026
  // architecture modernizes with the AI economy AND with each passing decade
  ws.shininess = clamp(0.3 + ai / 60 + yrs * 0.012, 0, 1)
  ws.flyingCars = clamp((ai - 25) / 35, 0, 1)
  ws.buildingHeight = clamp(0.45 + ai / 45 + yrs * 0.025, 0.2, 1.6)
  ws.tents = clamp(homeless / 12000, 0, 1)
  const has = k => adopted.includes(k)
  if (quake || has('quake')) { ws.mood = 'quake'; ws.weather = 'dust'; ws.destruction = 0.9; ws.fog = 0.6; thesis = `${ind.year}: post-quake SF — towers buckle, ${Math.round(homeless).toLocaleString()} unhoused, rebuilding politics dominate.` }
  else if (has('influx')) { ws.mood = 'decline'; ws.tents = clamp(ws.tents + 0.45, 0, 1); ws.fog = 0.45; thesis = `${ind.year}: a homeless influx — ${Math.round(homeless).toLocaleString()} unhoused, encampments spread, shelters overrun.` }
  else if (has('aiboom')) { ws.mood = 'utopia'; ws.weather = 'clear'; ws.shininess = 1; ws.flyingCars = 1; ws.buildingHeight = clamp(ws.buildingHeight + 0.4, 0, 1.6); thesis = `${ind.year}: AI-boom SF — gleaming supertalls, flying transit, automation at ${ai.toFixed(0)}%.` }
  else if (has('regulated')) { ws.mood = 'regulated'; ws.shininess = 0.5; ws.flyingCars = 0.15; thesis = `${ind.year}: regulated SF — AI capped, a deliberate human-paced city.` }
  else if (has('snow')) { ws.mood = 'decline'; ws.weather = 'snow'; ws.fog = 0.5; ws.greenery = 0.1; thesis = `${ind.year}: a frozen SF — rare snow blankets the hills.` }
  else if (has('flood')) { ws.mood = 'decline'; ws.weather = 'rain'; ws.fog = 0.7; ws.greenery = 0.5; thesis = `${ind.year}: flooded SF — water at the margins, storms rolling in.` }
  else if (has('fire')) { ws.mood = 'decline'; ws.weather = 'smog'; ws.fog = 0.6; ws.destruction = 0.35; thesis = `${ind.year}: wildfire smoke chokes the SF skyline.` }
  else if (has('housing') || has('prosperity')) { ws.mood = 'recovery'; ws.tents = clamp(ws.tents - 0.35, 0, 1); ws.greenery = 0.6; thesis = `${ind.year}: recovering SF — ${Math.round(ind.housingBuilt?.value || 0).toLocaleString()} units built, encampments shrinking.` }
  else if (ai > 45) { ws.mood = seed % 2 ? 'utopia' : 'decline'; ws.buildingHeight = clamp(ws.buildingHeight + 0.2, 0, 1.6); thesis = ws.mood === 'utopia' ? `${ind.year}: automation at ${ai.toFixed(0)}% — supertall prosperity, unevenly shared.` : `${ind.year}: automation at ${ai.toFixed(0)}% — hollowed-out middle, rising need.` }
  else { ws.mood = 'neutral'; thesis = `${ind.year}: AI exposure ${ai.toFixed(0)}%, jobs ${(ind.jobs?.value || 0).toFixed(0)}% — the city reshapes around automation.` }
  ws.timeOfDay = [0.32, 0.5, 0.7, 0.85][seed % 4]
  ws.buildingDensity = clamp(ws.buildingDensity + (seed - 1.5) * 0.06, 0.2, 1)
  ws.greenery = clamp(ws.greenery + (seed % 2 ? 0.1 : -0.05), 0, 1)
  return { ws, thesis }
}

function divergence(agents) {
  const keys = ['buildingDensity', 'buildingHeight', 'shininess', 'flyingCars', 'tents', 'destruction', 'greenery']
  return agents.map(a => {
    let tot = 0, n = 0
    for (const b of agents) { if (a === b) continue; let d = 0; for (const k of keys) d += Math.abs((a.worldState[k] || 0) - (b.worldState[k] || 0)); tot += d / keys.length; n++ }
    return n ? +(tot / n).toFixed(3) : 0
  })
}

export function tick(state, deltaSeconds = SECONDS_PER_MONTH, force = false) {
  const ind = state.projection.indicators
  const prev = state.monthsElapsed
  state.simSeconds += deltaSeconds
  const months = Math.floor(state.simSeconds / SECONDS_PER_MONTH) - prev
  if (months > 0) { advanceIndicators(ind, months); state.monthsElapsed += months }

  state.agents.forEach((a, idx) => {
    a.status = 'building'
    // one-time events fade after ~18 sim-months so the world moves on instead
    // of replaying the same reaction forever.
    a.variables = a.variables.filter(v => state.monthsElapsed - v.ts < 18)
    const adopted = a.variables.map(v => v.key)
    const r = interpret(ind, adopted, idx)
    a.worldState = r.ws; a.thesis = r.thesis; a.interpretation = r.thesis
  })
  const dm = divergence(state.agents)
  state.agents.forEach((a, i) => {
    a.divergence = dm[i]
    const target = state.agents[(i + 1) % 4]
    a.lastCritiqueGiven = { targetWindow: target.window, dimension: dm[i] > 0.25 ? 'coherence' : 'novelty', claim: dm[i] > 0.25 ? `W${target.window} reads the same data differently.` : `W${target.window} is converging — push a distinct hypothesis.` }
    target.lastCritiqueReceived = { from: a.window, ...a.lastCritiqueGiven }
    a.improvementScore = clamp(a.improvementScore + 0.02, 0, 1)
    a.status = 'idle'
  })

  const crossed = Math.floor(state.monthsElapsed / CHANGELOG_EVERY_MONTHS) > Math.floor(prev / CHANGELOG_EVERY_MONTHS)
  if (crossed || force) {
    const sd = `${ind.year}-${String((ind.month % 12) + 1).padStart(2, '0')}`
    // one consolidated entry per period showing the REAL evolving cross-domain
    // numbers — never a repeated canned string.
    for (const a of state.agents) state.changelog.unshift({ ts: state.monthsElapsed, simDate: sd, window: a.window, kind: force ? 'event-reaction' : 'projection', thesis: dataLine(ind, a), mood: a.worldState.mood, divergence: a.divergence })
    state.changelog = state.changelog.slice(0, 300)
  }
  return state
}

// Real cross-domain data line — changes every period as indicators project.
export function dataLine(ind, a) {
  if (!ind.population) return a.thesis
  const f = (n) => Math.round(n).toLocaleString()
  return `pop ${f(ind.population.value)} · jobs ${ind.jobs.value.toFixed(1)}% · AI-exposure ${ind.aiDisplacement.value.toFixed(0)}% · homeless ${f(ind.homelessness.value)} · housing +${f(ind.housingBuilt.value)} — ${a.worldState.mood}`
}

export function injectVariable(state, rawText) {
  const p = classifyMessage(rawText)
  const key = eventKey(p.clean)
  const targets = p.global ? [1, 2, 3, 4] : p.targets
  const ind = state.projection.indicators
  // GLOBAL events shift the shared real-data dashboard (affect all windows).
  // TARGETED (@N) events are that window's OWN scenario — they do NOT move the
  // shared indicators, so an "@1 earthquake" only hits window 1.
  if (p.global) applyEvent(ind, key, 1, parseQty(p.clean))
  const sd = `${ind.year}-${String((ind.month % 12) + 1).padStart(2, '0')}`
  state.events.unshift({ id: 'evt-' + state.events.length, rawText: p.clean, key, targets, global: p.global, simDate: sd, ts: state.monthsElapsed })
  for (const a of state.agents) if (targets.includes(a.window)) { a.variables.unshift({ clean: p.clean, key, ts: state.monthsElapsed }); a.variables = a.variables.slice(0, 8) }
  state.changelog.unshift({ ts: state.monthsElapsed, simDate: sd, window: p.global ? 'ALL' : targets.join(','), kind: 'injection', thesis: p.clean })
  return { parsed: p, targets }
}
